import { gql, useQuery } from '@apollo/client'
import { RouteComponentProps } from '@reach/router'
import { format } from 'date-fns'
import { Link } from 'gatsby'
import { VisionManifestQuery, VisionManifestQueryVariables } from './__generated__/VisionManifestQuery'

const manifestQuery = gql`
  query VisionManifestQuery($id: ID!) {
    visionManifestById(id: $id) {
      id
      receivedDate
      subject
      from
      contentText
      totalItems
      ignoredItems
      b2bItems
      b2cItems
      rows {
        id
        action
        kitPK
        template
        reason
        ref
        dispatchInfo {
          dispatchDate
          trackingNo
          service
          extraInfo
        }
      }
    }
  }
`
const formatDate = (dateString: string) => format(new Date(dateString), 'dd MMM yyyy, hh:mm aa')

const VisionManifest = ({ manifestId }: RouteComponentProps<{ manifestId: string }>) => {
  const { loading, error, data } = useQuery<VisionManifestQuery, VisionManifestQueryVariables>(manifestQuery, {
    variables: { id: manifestId },
  })

  if (loading) {
    return <div>Loading...</div>
  }

  if (error) {
    return <div>Error! {error.message}</div>
  }

  const manifest = data.visionManifestById

  return (
    <>
      <section className="section">
        <div className="container">
          <h1 className="title">Vision Manifest {formatDate(manifest.receivedDate)}</h1>

          <p>
            <strong>From: </strong> {manifest.from}
          </p>

          <p>
            <strong>Subject: </strong> {manifest.subject}
          </p>

          <p>
            <strong>Content: </strong> {manifest.contentText}
          </p>
        </div>
      </section>

      <section className="section mt-5">
        <nav className="level has-width-unset">
          <div className="level-item has-text-centered">
            <div>
              <p className="heading">Manifest Items</p>
              <p className="title">{manifest.rows.length}</p>
            </div>
          </div>

          <div className="level-item has-text-centered">
            <div>
              <p className="heading">Ignored</p>
              <p className="title">{manifest.ignoredItems}</p>
            </div>
          </div>

          <div className="level-item has-text-centered">
            <div>
              <p className="heading">B2B</p>
              <p className="title">{manifest.b2bItems}</p>
            </div>
          </div>

          <div className="level-item has-text-centered">
            <div>
              <p className="heading">B2C</p>
              <p className="title">{manifest.b2cItems}</p>
            </div>
          </div>
        </nav>

        <div className="container">
          <table className="table is-bordered is-fullwidth">
            <thead>
              <tr>
                <th>Item</th>
                <th>Action</th>
                <th>Dispatch Info</th>
              </tr>
            </thead>

            <tbody>
              {manifest.rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.kitPK ? <Link to={`/dashboard/kits?kitId=${row.kitPK}`}>{row.kitPK}</Link> : row.ref}</td>
                  <td>
                    {row.action === 'EMAIL' && (
                      <>
                        Email sent: <strong>{row.template}</strong>
                      </>
                    )}

                    {row.action === 'IGNORE' && <span className="has-text-grey">Ignored: ({row.reason})</span>}

                    {row.action === 'WARN' && (
                      <>
                        <span className="has-text-danger">Warning</span>: ({row.reason})
                      </>
                    )}
                  </td>
                  <td>
                    {row.dispatchInfo.service}{' '}
                    <a
                      href={`https://www.dpd.co.uk/apps/tracking/?reference=${encodeURIComponent(
                        row.dispatchInfo.trackingNo
                      )}#results`}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {row.dispatchInfo.trackingNo}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  )
}

export default VisionManifest
